import React, { useContext, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Context } from 'Context/Context'
import MiniAvatar from 'Components/Icons/MiniAvatar'

const sampleFirms = [
  { name: 'Slack Davis Sanger LLP', rating: '4.8/5', blurb: 'Recovered hundreds of millions in complex commercial and personal injury litigation.' },
  { name: 'Thomas J. Henry Law', rating: '4.7/5', blurb: 'Secured a $1.25 billion verdict in a catastrophic personal injury case.' },
  { name: 'Howry, Breen & Herman', rating: '4.9/5', blurb: 'Secured record-setting trial verdicts in commercial and personal injury litigation.' },
]

const matchedCases = [
  { id: 'QCR-1058-D · Brooklyn', match: '94% match', title: 'Catastrophic injury, commercial vehicle', range: '$1.24M – $4.76M', stats: 'Strength 78 · Docs 61', highlight: true },
  { id: 'QCR-1054-K · Queens', match: '87% match', title: 'Premises liability, structural collapse', range: '$310K – $890K', stats: 'Strength 71 · Docs 82' },
  { id: 'QCR-1049-B · Bronx', match: '83% match', title: 'Medical malpractice, delayed diagnosis', range: '$620K – $2.1M', stats: 'Strength 66 · Docs 74' },
]

const clientSteps = [
  { num: '01', title: 'Tell us what happened', body: 'A guided intake covering the incident, injuries, treatment, and any documents you already have. Save and return any time.' },
  { num: '02', title: 'Get your case review', body: 'A settlement range, scores for case strength and documentation, and the gaps a defense lawyer would find.' },
  { num: '03', title: 'Choose your counsel', body: 'Firms request access to your case details; you approve who sees your file.' },
]

const firmSteps = [
  { num: '01', title: 'Set your criteria', body: 'Practice areas, jurisdictions, and minimum case value.' },
  { num: '02', title: 'Review the record', body: 'Anonymized file with valuation, scores, evidence ledger, and risk flags. No cost to look.' },
  { num: '03', title: 'Clear conflicts', body: 'Clear conflicts before you can see case details.' },
  { num: '04', title: 'Close case', body: 'Sign up client and close case on platform. Case details are deleted forever within 7 days.' },
]

const funderSteps = [
  { num: '01', text: 'Request a funder directly from the case file' },
  { num: '02', text: 'The funder reviews your request and the case record' },
  { num: '03', text: 'Finalize funding terms together and proceed with the case' },
]

const clientFaqs = [
  { q: 'Which states do you cover?', a: 'Right now Quick Case Review is for cases in Texas only.' },
  { q: 'Will my name or contact information be shared with anyone?', a: 'No. You can post your case anonymously, your name is never collected. Firms only see the case details, and nothing about you personally until you choose to share it.' },
  { q: 'Is it really free, and is there any obligation?', a: "Yes, free, and no. Getting your estimate and posting your case costs nothing, and you're never required to hire anyone. Firms pay to access the platform, not you." },
  { q: 'What happens to my information after I submit?', a: "It's used only to generate your case review and match you with firms, never sold, and only law firm case managers and attorneys registered on the platform can ever view it." },
  { q: 'Do I have to talk to anyone right away?', a: 'No. You get your estimate immediately, on your own, with no call required. Firms can only request more information, and you decide if and when to respond.' },
  { q: 'How accurate is the estimate, and do I have to accept a matched firm?', a: "The range is modeled based on various factors, and is meant as a starting point, not a guarantee. And no, you can review requests from multiple firms and choose the one that's right for you, or none at all." },
]

const firmFaqs = [
  { q: 'Which states do you cover?', a: 'Right now all cases on Quick Case Review are from Texas.' },
  { q: 'How much does it cost?', a: 'Browsing and requesting cases is free. Premium is a flat $10/user/month — no per-lead auction, no bidding against other firms.' },
  { q: 'How do I know a case is legitimate?', a: 'Every case includes a modeled valuation, a scored evidence record, and flagged risks before you spend any time on intake.' },
  { q: 'What happens after I request a case?', a: "The client reviews your request and approves who sees their contact details — you're never given information without their consent." },
  { q: 'Can I get funding on a case I take?', a: 'Premium adds priority access to premium cases and premium data.' },
  { q: 'Am I competing with other firms for the same case?', a: "Cases aren't exclusive. Several firms may request the same case, and the client chooses who to approve. We don't auction leads or rank you by how much you spend." },
  { q: 'How do you handle conflicts of interest?', a: "You only see case information after the client approves you. If it's a conflict, you mark it yourself and won't see that case again." },
  { q: 'Are these real, qualified cases, or spam leads?', a: 'Every case is a client-submitted intake with a modeled valuation and scored record attached, not a scraped or resold lead list.' },
]

function LogoMark({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className="flex-none block">
      <path d="M4.4 12a7.6 7.6 0 1 0 2.6-5.75" stroke="#c9a26a" strokeWidth="2.1" strokeLinecap="round" />
      <path d="M3.5 4.6v4.2h4.2" stroke="#c9a26a" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 8.1V12l-2.7 2.1" stroke="#c9a26a" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function FaqList({ title, items }) {
  return (
    <div className="bg-ivory px-5 py-12 lg:px-16 lg:py-14 border-t border-navy/10">
      <div className="max-w-[900px] mx-auto">
        <h2 className="font-spectral text-navy text-[24px] lg:text-[32px] mb-6 lg:mb-7">{title}</h2>
        <div className="flex flex-col">
          {items.map((item, i) => (
            <div key={item.q} className={'py-4 lg:py-[18px]' + (i < items.length - 1 ? ' border-b border-navy/10' : '')}>
              <div className="font-plexsans font-medium text-[14px] lg:text-[14.5px] text-navy mb-1.5">{item.q}</div>
              <div className="font-plexsans text-[12.5px] lg:text-[13px] leading-[1.65] text-navy/60">{item.a}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function SiteFooter() {
  return (
    <div className="bg-ivory px-5 py-9 lg:px-16 border-t border-navy/[0.14]">
      <div className="max-w-[1320px] mx-auto">
        <div className="flex flex-wrap justify-center gap-5 lg:gap-9 mb-5 font-plexsans font-medium text-[13px]">
          <Link to="/privacy-policy" className="text-navy underline">Privacy Policy</Link>
          <Link to="/user-agreement" className="text-navy underline">User Agreement</Link>
          <Link to="/contact-us" className="text-navy underline">Contact Us</Link>
        </div>
        <p className="mb-4 text-center font-plexsans text-[11px] leading-[1.7] text-navy/40">
          Warning: The various computing functionalities, computing communications, computing interfaces, etc., of this Platform are protected with multiple U.S. patents and patent applications. The founder, who filed her first patent application in 2019 and is domiciled in the Eastern District of Texas, is a prolific inventor and patent filer who holds multiple patents and patent applications in several technologies. Intentional infringement of such patents may result in treble damages.
        </p>
        <p className="text-center font-plexsans text-[11.5px] leading-[1.6] text-navy/45">
          © 2026 Quick Case Review. All rights reserved. Estimates are modeled from public data, are not legal advice, and do not create an attorney–client relationship.
        </p>
      </div>
    </div>
  )
}

function ClientPanel({ navigate, howItWorksRef }) {
  return (
    <div className="bg-cream">
      <div className="px-5 py-12 lg:px-16 lg:py-20">
        <div className="max-w-[1320px] mx-auto grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-14 items-center">
          <div>
            <div className="font-plexmono text-[10px] lg:text-[10.5px] tracking-[0.18em] uppercase text-tan mb-4">Free, instant case review · Texas</div>
            <h1 className="font-spectral font-normal text-navy text-[34px] lg:text-[52px] leading-[1.16] lg:leading-[1.14] tracking-[-0.015em] mb-4 lg:mb-5">Know what your case is worth before you call a lawyer.</h1>
            <p className="font-plexsans text-[14px] lg:text-[15.5px] leading-[1.7] text-navy/60 max-w-[48ch] mb-6 lg:mb-7">Describe the incident once. We model a settlement range and match you with firms that have taken on cases like yours.</p>
            <div className="mb-6">
              <button onClick={() => navigate('/client/cases')} className="font-plexsans font-medium text-[14px] text-ivory bg-tan hover:bg-tanDark rounded-sm px-7 py-4 transition-colors cursor-pointer">
                Get my free estimate
              </button>
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-2 font-plexsans text-[11.5px] text-navy/50">
              <span>For cases in Texas</span><span>·</span><span>Takes about 6 minutes</span><span>·</span><span>Identity hidden by default</span><span>·</span><span>No fee to you, ever</span>
            </div>
          </div>

          <div className="bg-navy rounded text-cream p-7 lg:p-8">
            <div className="font-plexmono text-[10px] tracking-[0.16em] uppercase text-cream/50 mb-4">Your estimated settlement range</div>
            <div className="flex items-baseline gap-3 mb-2 flex-wrap">
              <span className="font-spectral font-light text-[32px] lg:text-[42px] text-ivory tabular-nums">$1,000,000</span>
              <span className="font-spectral text-[15px] text-cream/50">to</span>
              <span className="font-spectral font-light text-[32px] lg:text-[42px] text-ivory tabular-nums">$5,000,000</span>
            </div>
            <div className="font-plexsans text-[12.5px] leading-[1.6] text-cream/55">Median comparable outcome <span className="text-gold">$3,000,000</span></div>
            <div className="relative h-9 mt-6 mb-1.5">
              <div className="absolute top-3 left-0 right-0 h-[2px] bg-cream/[0.18]"></div>
              <div className="absolute top-[11px] left-[12%] w-[48%] h-1 bg-gold"></div>
              <div className="absolute top-1.5 left-[31%] w-[2px] h-3.5 bg-ivory"></div>
              <div className="absolute top-6 left-[12%] -translate-x-1/2 font-plexmono text-[10.5px] text-cream/65">Low</div>
              <div className="absolute top-6 left-[31%] -translate-x-1/2 font-plexmono text-[10.5px] text-gold">Likely</div>
              <div className="absolute top-6 left-[60%] -translate-x-1/2 font-plexmono text-[10.5px] text-cream/65">High</div>
            </div>
            <div className="flex mt-5 pt-4 border-t border-cream/[0.14] flex-wrap gap-y-3">
              <div className="flex-1 pr-4 min-w-[90px]">
                <div className="font-plexmono text-[9.5px] tracking-[0.1em] uppercase text-cream/45 mb-1.5">Confidence</div>
                <div className="font-spectral text-[17px] text-ivory">8/10</div>
              </div>
              <div className="flex-1 px-4 border-l border-cream/[0.14] min-w-[90px]">
                <div className="font-plexmono text-[9.5px] tracking-[0.1em] uppercase text-cream/45 mb-1.5">Recovery chance</div>
                <div className="font-spectral text-[17px] text-ivory">95%</div>
              </div>
              <div className="flex-1 pl-4 border-l border-cream/[0.14] min-w-[90px]">
                <div className="font-plexmono text-[9.5px] tracking-[0.1em] uppercase text-cream/45 mb-1.5">Settlement chance</div>
                <div className="font-spectral text-[17px] text-ivory">80%</div>
              </div>
            </div>
            <div className="flex items-center gap-2.5 mt-4 pt-4 border-t border-cream/[0.14] flex-wrap">
              <span className="font-plexmono text-[10px] tracking-[0.08em] uppercase text-cream/50">Early settlement</span>
              <span className="font-plexsans font-medium text-[10.5px] text-navy bg-gold px-2.5 py-1 rounded-sm whitespace-nowrap">Strongly recommended</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-ivory px-5 py-10 lg:px-16 lg:py-12 border-t border-navy/10">
        <div className="max-w-[1320px] mx-auto">
          <div className="flex items-baseline justify-between gap-3 mb-5 flex-wrap">
            <h2 className="font-spectral text-navy text-[24px] lg:text-[32px]">Firms matched to a case like yours</h2>
            <span className="font-plexmono text-[11px] lg:text-[11.5px] tracking-wider uppercase text-tan">Sample match</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-5">
            {sampleFirms.map((firm) => (
              <div key={firm.name} className="bg-cream rounded p-5">
                <div className="flex items-baseline justify-between gap-2 mb-2">
                  <span className="font-plexsans font-semibold text-[14px] text-navy">{firm.name}</span>
                  <span className="font-plexsans font-medium text-[12px] text-tan">{firm.rating}</span>
                </div>
                <div className="font-plexmono text-[10.5px] tracking-wider uppercase text-navy/45 mb-1.5">Largest verdict/settlement</div>
                <div className="font-plexsans text-[12.5px] leading-[1.6] text-navy/65">{firm.blurb}</div>
              </div>
            ))}
          </div>
          <div className="text-center mt-6">
            <button onClick={() => navigate('/client/cases')} className="font-plexsans font-medium text-[13px] text-tan underline cursor-pointer">
              See recommended firms for your case →
            </button>
          </div>
        </div>
      </div>

      <div ref={howItWorksRef} className="bg-ivory px-5 py-12 lg:px-16 lg:py-16 border-t border-navy/10">
        <div className="max-w-[1320px] mx-auto">
          <h2 className="font-spectral text-navy text-[24px] lg:text-[32px] mb-7 lg:mb-8">How it works</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-0">
            {clientSteps.map((step, i) => (
              <div
                key={step.title}
                className={
                  'lg:text-center' +
                  (i < clientSteps.length - 1 ? ' lg:border-r lg:border-navy/10' : '') +
                  (i === 0 ? ' lg:pr-9' : i === 1 ? ' lg:px-9' : ' lg:pl-9')
                }
              >
                <div className="font-spectral text-[32px] lg:text-[40px] text-tan mb-3 lg:mb-4">{step.num}</div>
                <h3 className="font-spectral text-navy text-[19px] lg:text-[22px] mb-2">{step.title}</h3>
                <p className="font-plexsans text-[13px] lg:text-[14px] leading-[1.7] lg:leading-[1.75] text-navy/60">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <FaqList title="Common questions" items={clientFaqs} />
    </div>
  )
}

function FirmPanel({ navigate, howItWorksRef }) {
  return (
    <div className="bg-ivory">
      <div className="bg-navy px-5 py-12 lg:px-16 lg:py-16 text-cream">
        <div className="max-w-[1320px] mx-auto grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-14 items-center">
          <div>
            <div className="font-plexmono text-[10px] lg:text-[10.5px] tracking-[0.18em] uppercase text-gold mb-4">For Texas plaintiff firms</div>
            <h1 className="font-spectral font-normal text-ivory text-[34px] lg:text-[54px] leading-[1.14] lg:leading-[1.12] tracking-[-0.015em] mb-4 lg:mb-5">Case flow that arrives already documented.</h1>
            <p className="font-plexsans text-[14px] lg:text-[16.5px] leading-[1.7] lg:leading-[1.75] text-cream/60 max-w-[52ch] mb-6 lg:mb-7">Every file reaches you with a modeled valuation, a scored evidence record, and flagged risks. Filter by practice area, jurisdiction, and case value, and request potential client for full case details.</p>
            <div className="flex flex-wrap gap-3 mb-6">
              <button className="font-plexsans font-medium text-[14px] text-ivory bg-tan hover:bg-tanDark rounded-sm px-7 py-4 transition-colors cursor-pointer">
                Request a demo
              </button>
              <button onClick={() => navigate('/signin/2')} className="font-plexsans font-medium text-[13.5px] text-cream bg-transparent border border-cream/[0.28] rounded-sm px-6 py-4 cursor-pointer">
                Attorney sign in
              </button>
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-2 font-plexsans text-[11.5px] text-cream/45">
              <span>No per-lead pricing</span><span>·</span><span>Conflicts check before contact</span><span>·</span><span>Cancel any time</span>
            </div>
          </div>

          <div className="bg-ivory rounded p-6 lg:p-7">
            <div className="flex items-baseline justify-between gap-2 pb-3.5 border-b border-navy/[0.14] mb-4 flex-wrap">
              <span className="font-plexmono text-[10px] tracking-[0.14em] uppercase text-navy/50">Matched cases today</span>
              <span className="font-plexmono text-[10px] tracking-[0.1em] uppercase text-tan">12 new</span>
            </div>
            <div className="flex flex-col gap-3">
              {matchedCases.map((c) => (
                <div
                  key={c.id}
                  className={'border border-navy/[0.14] rounded p-3.5' + (c.highlight ? ' border-l-2 border-l-tan rounded-l-none' : '')}
                >
                  <div className="flex items-baseline justify-between gap-2 mb-1.5 flex-wrap">
                    <span className="font-plexmono text-[11px] text-navy/45">{c.id}</span>
                    <span className={'font-plexmono font-medium text-[11px]' + (c.highlight ? ' text-tan' : ' text-navy/50')}>{c.match}</span>
                  </div>
                  <div className="font-spectral text-[16px] leading-[1.3] text-navy mb-2">{c.title}</div>
                  <div className="flex justify-between font-plexsans text-[12px] text-navy/60 flex-wrap gap-1">
                    <span>{c.range}</span><span>{c.stats}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div ref={howItWorksRef} className="bg-ivory px-5 py-12 lg:px-16 lg:py-16">
        <div className="max-w-[1320px] mx-auto">
          <h2 className="font-spectral text-navy text-[24px] lg:text-[32px] mb-7 lg:mb-8">Your process, on the platform</h2>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-0">
            {firmSteps.map((step, i) => (
              <div
                key={step.title}
                className={
                  (i < firmSteps.length - 1 ? ' lg:border-r lg:border-navy/10' : '') +
                  (i === 0 ? ' lg:pr-7' : i === firmSteps.length - 1 ? ' lg:pl-7' : ' lg:px-7')
                }
              >
                <div className="font-plexmono text-[12px] text-tan mb-3.5">{step.num}</div>
                <h3 className="font-spectral text-navy text-[19px] mb-2">{step.title}</h3>
                <p className="font-plexsans text-[13px] leading-[1.7] text-navy/60">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-ivory px-5 py-10 lg:px-16 lg:py-14 border-t border-navy/10">
        <div className="max-w-[1320px] mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-8 lg:gap-12 items-start">
          <div>
            <div className="font-plexmono text-[10px] tracking-[0.16em] uppercase text-tan mb-3">For law firms</div>
            <h2 className="font-spectral text-navy text-[24px] lg:text-[32px] mb-2.5">Get funded on cases you take</h2>
            <p className="font-plexsans text-[13px] lg:text-[13.5px] leading-[1.7] text-navy/60 max-w-[44ch]">Connect with litigation funders through the platform to move on a case without waiting on cash flow. Request a funder, they review the file, you finalize terms directly.</p>
          </div>
          <div className="flex flex-col border-t border-navy/10">
            {funderSteps.map((step) => (
              <div key={step.num} className="flex gap-4 py-4 border-b border-navy/10">
                <span className="font-plexmono text-[12px] text-tan flex-none w-5">{step.num}</span>
                <span className="font-plexsans text-[13.5px] leading-[1.6] text-navy">{step.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-cream px-5 py-12 lg:px-16 lg:py-16 border-t border-navy/10">
        <div className="max-w-[1320px] mx-auto">
          <div className="max-w-[62ch] mb-8">
            <h2 className="font-spectral text-navy text-[24px] lg:text-[32px] mb-2">Membership</h2>
            <p className="font-plexsans text-[13px] lg:text-[14px] leading-[1.7] text-navy/60">Browse and request cases for free. Premium adds priority access to premium cases and premium data.</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.15fr] gap-5 items-stretch">
            <div className="bg-ivory border border-navy/[0.14] rounded p-7 flex flex-col">
              <div className="font-plexmono text-[10px] tracking-[0.16em] uppercase text-navy/50 mb-3.5">Free · For professionals</div>
              <div className="flex items-baseline gap-1.5 mb-2">
                <span className="font-spectral font-light text-[36px] lg:text-[40px] text-navy">$0.00</span>
                <span className="font-plexsans text-[13px] text-navy/50">per user / month</span>
              </div>
              <p className="font-plexsans text-[12.5px] leading-[1.6] text-navy/55 mb-5">Everything you need to work cases you find yourself.</p>
              <button className="w-full font-plexsans font-medium text-[13px] text-navy bg-ivory border border-navy/[0.22] rounded-sm py-3.5 mb-6 cursor-pointer">
                Create free account
              </button>
              <div className="font-plexmono text-[10px] tracking-[0.14em] uppercase text-navy/45 pb-3.5 border-b border-navy/10 mb-4">Active features</div>
              <div className="flex flex-col gap-3">
                {['Limited access to the latest cases', 'Streamlined case approvals', 'Unlimited case requests', 'Unlimited leads', 'Limited data access'].map((f) => (
                  <div key={f} className="flex gap-2.5">
                    <span className="flex-none text-tan font-plexsans text-[12.5px] leading-[1.6]">—</span>
                    <div className="font-plexsans font-medium text-[12.5px] leading-[1.5] text-navy">{f}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-navy rounded p-7 text-cream relative flex flex-col">
              <div className="absolute top-0 right-7 font-plexmono font-medium text-[9.5px] tracking-[0.14em] uppercase text-navy bg-gold px-2.5 py-1.5 rounded-b-sm">Recommended</div>
              <div className="font-plexmono text-[10px] tracking-[0.16em] uppercase text-cream/55 mb-3.5">Premium · For professionals</div>
              <div className="flex items-baseline gap-1.5 mb-2">
                <span className="font-spectral font-light text-[36px] lg:text-[40px] text-ivory">$10.00</span>
                <span className="font-plexsans text-[13px] text-cream/50">per user / month</span>
              </div>
              <p className="font-plexsans text-[12.5px] leading-[1.6] text-cream/60 mb-5">First access to new cases, priority on urgent matters, and the complete client data record.</p>
              <button onClick={() => navigate('/attorney/subscription')} className="w-full font-plexsans font-medium text-[13px] text-navy bg-gold hover:bg-goldDark rounded-sm py-3.5 mb-6 cursor-pointer transition-colors">
                Start premium
              </button>
              <div className="font-plexmono text-[10px] tracking-[0.14em] uppercase text-cream/50 pb-3.5 border-b border-cream/[0.16] mb-4">Everything in Free, plus</div>
              <div className="flex flex-col gap-3">
                {['Access to the latest cases', 'Priority for time-sensitive cases (SOS)', 'Automatic pre-approval listing', 'Streamlined case approvals', 'Unlimited case requests', 'Unlimited leads', 'Premium data access'].map((f) => (
                  <div key={f} className="flex gap-2.5">
                    <span className="flex-none text-gold font-plexsans text-[12.5px] leading-[1.6]">—</span>
                    <div className="font-plexsans font-medium text-[12.5px] leading-[1.5] text-ivory">{f}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <FaqList title="Common questions" items={firmFaqs} />
    </div>
  )
}

function HomeToggleHero() {
  const [audience, setAudience] = useState('client')
  const navigate = useNavigate()
  const { userDetails } = useContext(Context)
  const isLoggedIn = !!userDetails?.email
  const howItWorksRef = useRef(null)
  const isClient = audience === 'client'

  const scrollToHowItWorks = () => {
    howItWorksRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <div className="w-full font-plexsans">
      <div className="bg-navy px-5 py-3.5 lg:px-10 lg:py-4">
        {/* Mobile: logo + Log in on one row, the For clients / For law firms toggle
            full width underneath. Desktop: logo · links … toggle · Log in on one row. */}
        <div className="flex items-center flex-wrap gap-x-4 gap-y-3 lg:gap-x-3.5 max-w-[1320px] mx-auto">
          <div className="order-1 flex items-center gap-4 lg:gap-6 flex-none">
            <Link to="/" className="flex items-center gap-2 flex-none whitespace-nowrap">
              <LogoMark />
              <span className="flex items-start gap-1">
                <span className="font-plexsans font-semibold text-[11px] lg:text-[11.5px] tracking-wide uppercase text-cream">Quick Case Review</span>
                <span className="font-plexmono font-medium text-[6.5px] tracking-widest uppercase text-gold mt-px">Beta</span>
              </span>
            </Link>
            <div className="hidden lg:flex gap-4 font-plexsans text-[12px] whitespace-nowrap">
              <button onClick={() => navigate('/client/cases')} className="text-gold cursor-pointer">Find a law firm</button>
              <button onClick={() => setAudience('firm')} className="text-gold cursor-pointer">Find a case</button>
              <button onClick={scrollToHowItWorks} className="text-cream/60 cursor-pointer">How it works</button>
            </div>
          </div>
          <div className="order-3 lg:order-2 w-full lg:w-auto lg:ml-auto flex gap-0.5 p-[3px] bg-cream/10 rounded whitespace-nowrap">
            <button
              onClick={() => setAudience('client')}
              className={'flex-1 lg:flex-none px-3 py-2.5 lg:py-2 lg:px-4 rounded-sm text-[12.5px] font-medium transition-colors cursor-pointer' + (isClient ? ' bg-gold text-navy' : ' text-cream/70')}
            >
              For clients
            </button>
            <button
              onClick={() => setAudience('firm')}
              className={'flex-1 lg:flex-none px-3 py-2.5 lg:py-2 lg:px-4 rounded-sm text-[12.5px] font-medium transition-colors cursor-pointer' + (!isClient ? ' bg-gold text-navy' : ' text-cream/70')}
            >
              For law firms
            </button>
          </div>
          <div className="order-2 lg:order-3 ml-auto lg:ml-0 flex items-center flex-none">
            {isLoggedIn ?
              // Signed in: avatar (initials/photo) opens the same account menu as the app header
              <div className="flex items-center pl-1" title="Your account">
                <MiniAvatar size={28} />
              </div>
            :
              <button onClick={() => navigate('/signin')} className="font-plexsans font-medium text-[12px] lg:text-[12.5px] text-navy bg-gold hover:bg-goldDark rounded-sm px-3.5 py-2 lg:px-4 lg:py-2.5 whitespace-nowrap cursor-pointer transition-colors">
                Log in
              </button>
            }
          </div>
        </div>
      </div>

      {isClient ? <ClientPanel navigate={navigate} howItWorksRef={howItWorksRef} /> : <FirmPanel navigate={navigate} howItWorksRef={howItWorksRef} />}

      <SiteFooter />
    </div>
  )
}

export default HomeToggleHero
